import type { EffectiveRole } from "../../hooks/useAuth";

export type TourPlacement = "left" | "right" | "top" | "bottom" | "center";

export type TourStep = {
  id: string;
  // CSS selector to find the target element. Missing target (or screens
  // narrower than the desktop sidebar breakpoint) falls back to a centered
  // modal — the tour is never blocked by a stale ref.
  targetSelector?: string;
  // Route to navigate to before searching for the target. The spotlight
  // re-measures after each navigation so the highlight tracks the new DOM.
  route?: string;
  title: string;
  body: string;
  // Preferred tooltip placement relative to the target. "center" or missing
  // target → modal-style centered tooltip.
  placement?: TourPlacement;
  // Optional final CTA. Pressing it navigates and closes the tour.
  cta?: { label: string; route: string };
};

const OWNER_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: "Bienvenido/a a Formular",
    body: "Te enseñamos en menos de un minuto dónde está cada cosa de tu panel. Puedes saltarlo en cualquier momento.",
    placement: "center",
  },
  {
    id: "overview",
    targetSelector: '[data-tour-id="nav-overview"]',
    route: "/",
    title: "Tu Overview",
    body: "Aquí ves de un vistazo tu plan, tus seats y tu rol en la organización.",
    placement: "right",
  },
  {
    id: "team",
    targetSelector: '[data-tour-id="nav-team"]',
    route: "/api/team",
    title: "Equipo",
    body: "Invita a los miembros de tu organización y asígnales un rol.",
    placement: "right",
  },
  {
    id: "usage",
    targetSelector: '[data-tour-id="nav-usage"]',
    route: "/api/usage",
    title: "Uso",
    body: "Sigue el consumo de créditos y storage de toda la organización en tiempo real.",
    placement: "right",
  },
  {
    id: "billing",
    targetSelector: '[data-tour-id="nav-billing"]',
    route: "/billing",
    title: "Facturación",
    body: "Gestiona tu plan, activa o cancela la suscripción, descarga facturas y mantén tu información fiscal al día.",
    placement: "right",
  },
  {
    id: "settings",
    targetSelector: '[data-tour-id="settings-button"]',
    title: "Ajustes",
    body: "Edita tu nombre, descarga tus datos (RGPD) o cierra tu cuenta.",
    placement: "right",
  },
  {
    id: "open-app",
    targetSelector: '[data-tour-id="open-app-button"]',
    title: "Abrir la app",
    body: "Este panel es tu cuenta. La app donde trabajas — chats, proyectos y skills — vive en app.formular.so. Este botón te lleva ahí en una pestaña nueva.",
    placement: "right",
  },
  {
    id: "cta-finish",
    title: "Todo listo",
    body: "Empieza invitando a tu equipo — sin ellos, la mitad de la magia no pasa.",
    placement: "center",
    cta: { label: "Invitar a mi equipo", route: "/api/team" },
  },
];

const MEMBER_STEPS: TourStep[] = [
  {
    id: "welcome",
    title: "Bienvenido/a a Formular",
    body: "Te enseñamos en 30 segundos qué encontrarás en tu panel. Puedes saltarlo en cualquier momento.",
    placement: "center",
  },
  {
    id: "overview",
    targetSelector: '[data-tour-id="nav-overview"]',
    route: "/",
    title: "Tu Overview",
    body: "Tu rol y la información básica de tu organización viven aquí.",
    placement: "right",
  },
  {
    id: "usage",
    targetSelector: '[data-tour-id="nav-usage"]',
    route: "/api/usage",
    title: "Uso",
    body: "Consulta tu consumo dentro del pool compartido de la organización.",
    placement: "right",
  },
  {
    id: "settings",
    targetSelector: '[data-tour-id="settings-button"]',
    title: "Ajustes",
    body: "Cambia tu nombre, descarga tus datos o cierra tu cuenta cuando quieras.",
    placement: "right",
  },
  {
    id: "open-app",
    targetSelector: '[data-tour-id="open-app-button"]',
    title: "Abrir la app",
    body: "Este panel es tu cuenta. Para entrar en la app de trabajo (chats, proyectos, skills) pulsa aquí — se abre en app.formular.so en una pestaña nueva.",
    placement: "right",
  },
  {
    id: "cta-finish",
    title: "Todo a tu alcance",
    body: "Ya conoces tu panel. Tu Overview tiene siempre los datos más importantes a mano.",
    placement: "center",
    cta: { label: "Ir a mi Overview", route: "/" },
  },
];

export function stepsForRole(role: EffectiveRole): TourStep[] {
  if (role === "enterprise_member") return MEMBER_STEPS;
  // enterprise_owner, institution → owner tour. admin/individual don't run
  // the tour at all (filtered upstream in the provider).
  return OWNER_STEPS;
}
